import { Flex, Box, Text, Button } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import CardHeader from "./CardHeader";
import Verification from "./Verification";
import Details from "./Details";
import { motion, useAnimation } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  getLastVerifiedAtDate,
  getTcDetailFoTrace,
} from "../../../Pages/traceability/Pages/api";

const Card = ({ id, traceVersion }) => {
  const containerControls = useAnimation();
  const contentControls = useAnimation();

  const [isCtaTriggered, setIsCtaTriggered] = useState(false);
  const [headerTitle, setHeaderTitle] = useState("");

  const { data: lastVerifiedAtDate, refetch: refetchLastVerifiedAt } = useQuery(
    {
      queryKey: ["lastVerifiedAt"],
      queryFn: () => getLastVerifiedAtDate(id, traceVersion),
    }
  );

  const commonButtonStyles = {
    width: "100%",
    padding: "1.5rem",
    fontWeight: "400",
  };

  useEffect(() => {
    if (!isCtaTriggered) {
      (async () => {
        await containerControls.start({ height: "auto" });
        await contentControls.start({ opacity: 1 });
      })();
    }
  }, [isCtaTriggered]);

  const triggerPageTransition = async () => {
    await contentControls.start({ opacity: 0 });
    await containerControls.start({ height: "5rem" });
  };

  const handleViewDetails = async () => {
    await triggerPageTransition();
    setHeaderTitle("Details");
    setIsCtaTriggered(true);
  };

  const handleVerifyOnBlockcahin = async () => {
    await triggerPageTransition();
    setHeaderTitle("Verifying on Blockcahin...");
    setIsCtaTriggered(true);
  };
  const [currentTcDetails, setCurrentTcDetails] = useState({});

  const getTcDetails = async (id) => {
    try {
      const res = await getTcDetailFoTrace(id);
      if (res?.status) {
        setCurrentTcDetails(res?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (id && traceVersion) {
      getTcDetails(id);
    }
  }, [id, traceVersion]);

  return (
    <Box
      background="#EDF3F8"
      margin={"1.5rem"}
      mt="2rem"
      padding=".25rem"
      paddingBlock="2rem"
      borderRadius="15px"
    >
      <motion.div
        initial={{ height: "5rem" }}
        animate={containerControls}
        transition={{ duration: 0.3 }}
        style={{
          overflow: "hidden",
          borderRadius: "30px",
          marginInline: "1rem",
        }}
      >
        {isCtaTriggered ? (
          <CardHeader
            id={id}
            setIsCtaTriggered={setIsCtaTriggered}
            triggerPageTransition={triggerPageTransition}
            title={headerTitle}
            containerControls={containerControls}
          >
            {headerTitle == "Details" ? (
              <Details id={id} />
            ) : (
              <Verification
                id={id}
                refetchLastVerifiedAt={refetchLastVerifiedAt}
              />
            )}
          </CardHeader>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={contentControls}
            transition={{ duration: 0.3 }}
          >
            <Box paddingInline="1rem">
              <Text fontSize="14px" fontWeight="700">
                Farmer to Fabric blockchain-based traceability
              </Text>
              <Text fontSize="14px" padding={"0.2rem"}>
                TC Number:{id}
              </Text>
              <Text fontSize="14px" padding={"0.2rem"}>
                Product Classification:{" "}
                {`${currentTcDetails?.data?.products?.[0]?.productId?.product_productClassification?.name} (${currentTcDetails?.data?.products?.[0]?.productId?.product_productCategory?.name})`}
              </Text>
              <Text fontSize="14px" padding={"0.2rem"}>
                Product Composition:{" "}
                {(() => {
                  const product = currentTcDetails?.data?.products?.[0];
                  const certified = product?.certifiedMaterial;
                  const nonCertified = product?.nonCertifiedMaterials;

                  const certifiedText = certified
                    ? `${certified.percentage}% ${certified.commodityId?.name} (organic)`
                    : "";

                  const nonCertifiedText =
                    Array.isArray(nonCertified) && nonCertified.length > 0
                      ? " + " +
                        nonCertified
                          .map(
                            (ncm) =>
                              `${ncm?.percentage}% ${ncm?.commodityId?.name}`
                          )
                          .join(" + ")
                      : "";

                  return `${certifiedText}${nonCertifiedText}`;
                })()}
              </Text>

              <Text fontSize="14px" padding={"0.2rem"}>
                Lot No: {currentTcDetails?.data?.products?.[0]?.lotNo}
              </Text>
            </Box>
            <Flex
              marginInline="1rem"
              mt={"1rem"}
              flexDirection="column"
              gap="1rem"
              alignItems="center"
            >
              <Button
                onClick={handleViewDetails}
                {...commonButtonStyles}
                border="1px solid #52bbff"
                color="#52bbff"
              >
                View Details
              </Button>
              <Button
                onClick={handleVerifyOnBlockcahin}
                {...commonButtonStyles}
                background="#52bbff"
                color="white"
              >
                Verify on Blockchain
              </Button>
              <Text color="gray" fontSize="12px" textAlign="center">
                Last Verified at: {lastVerifiedAtDate || "Loading..."}
              </Text>
            </Flex>
          </motion.div>
        )}
      </motion.div>
    </Box>
  );
};

export default Card;
